import { createAsyncThunk } from '@reduxjs/toolkit';
import { apiClient } from 'src/api/apiClient';
import {
  DELETE_CUSTOM_PROMPT,
  EDIT_CUSTOM_PROMPT,
  GENERATE_HPM,
  GET_ALL_CUSTOM_PROMPT,
  GET_CONTACT_DETAILS,
  GET_HPM_FILE_CONTENT_LIST,
  GET_HPM_DATA,
  GET_HPM_MSG_DATA,
  MARK_CUSTOM_PROMPT,
  REGENERATE_HPM,
  RETRY_HPM,
  SAVE_CUSTOM_PROMPT,
  UPDATE_HPM_DATA,
  GENERATE_HPM_FOR_FOLLOW_UPS,
  GENERATE_HPM_FOR_LINKEDIN_FOLLOW_UPS,
  GENERATE_FRESH_HPM,
  GENERATE_CALL_SCRIPT_MSG,
} from 'src/constants/httpEndPoints';
import {
  HPMCustomPromptProperties,
  HPMCustomLibraryProperties,
  SaveCustomPromptPayload,
  EditCustomPromptPayload,
  GenerateHpmPayload,
  FetchContactDetailsPayload,
  FetchMessagesPayload,
  SaveHpmdataPayload,
  RetryHpmPayloadType,
  RegenerateHpmPayloadType,
  GenerateHpmForFollowUpsPayloadType,
  GenerateHpmForPostLinkedinFollowUpsPayloadType,
} from './types';

const escapeHtml = (value: unknown): string => {
  const text = typeof value === 'string' ? value : value == null ? '' : String(value);
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

const toHtmlList = (items: unknown[]): string => {
  if (!Array.isArray(items) || items.length === 0) return '';
  return `<ul>${items
    .map((item) => `<li>${escapeHtml(item)}</li>`)
    .join('')}</ul>`;
};

const formatCallScriptMessage = (message: any): string => {
  if (typeof message === 'string') return message;
  if (!message || typeof message !== 'object') return '';

  const sections: string[] = [];
  if (message.opener) {
    sections.push(`<p><strong>Opener:</strong> ${escapeHtml(message.opener)}</p>`);
  }
  if (Array.isArray(message.talk_track) && message.talk_track.length > 0) {
    sections.push(`<p><strong>Talk Track:</strong></p>${toHtmlList(message.talk_track)}`);
  }
  if (Array.isArray(message.objection_handling) && message.objection_handling.length > 0) {
    const objectionItems = message.objection_handling.map((item: any) => {
      const objection = escapeHtml(item?.objection);
      const response = escapeHtml(item?.response);
      return `<li><strong>Objection:</strong> ${objection}<br/><strong>Response:</strong> ${response}</li>`;
    });
    sections.push(
      `<p><strong>Objection Handling:</strong></p><ul>${objectionItems.join('')}</ul>`,
    );
  }
  if (message.close) {
    sections.push(`<p><strong>Close:</strong> ${escapeHtml(message.close)}</p>`);
  }
  if (message.voicemail_fallback) {
    sections.push(
      `<p><strong>Voicemail Fallback:</strong> ${escapeHtml(message.voicemail_fallback)}</p>`,
    );
  }
  return sections.join('');
};

const normalizeMessageByType = (msgType: string, message: any): string => {
  if (msgType === 'callScriptMsg') {
    return formatCallScriptMessage(message);
  }
  if (typeof message === 'string') return message;
  return message == null ? '' : String(message);
};

// Helper function to transform fetchMessages response
const transformMessagesResponse = (responseData: any, contactPk: number) => {
  // Extract root-level arrays
  const rootActiveCustomer = responseData.activeCustomer || [];
  const rootFeatures = responseData.feature || [];
  const rootOutcomes = responseData.outcomes || [];
  const rootPainpoints = responseData.painpoints || [];
  const rootProductsAndServices = responseData.productsAndServices || [];
  const rootTestimonials = responseData.testimonials || [];
  
  // Merge all messages from different keys into a single object
  const mergedMessages: any = {
    emailMsg: null,
    linkedinPostMsg: null,
    followUpMsg: null,
    secondFollowUpMsg: null,
    postLinkedinFollowUpMsg: null,
    postLinkedinSecondFollowUpMsg: null,
    callScriptMsg: null,
  };

  // Process each message key (numeric IDs) and merge messages
  Object.keys(responseData).forEach(key => {
    // Skip non-numeric keys (root-level arrays)
    if (isNaN(Number(key))) return;

    const contactMessages = responseData[key];

    [
      'emailMsg',
      'linkedinPostMsg',
      'followUpMsg',
      'secondFollowUpMsg',
      'postLinkedinFollowUpMsg',
      'postLinkedinSecondFollowUpMsg',
      'callScriptMsg',
    ].forEach((msgType: string) => {
      if (contactMessages[msgType]) {
        const msg = contactMessages[msgType];

        // Mark selected items in arrays based on selectedPpfo
        const painpoint = rootPainpoints.map((item: any) => ({
          ...item,
          selected: msg.selectedPpfo?.includes(item.primaryKey) || false,
        }));

        const features = rootFeatures.map((item: any) => ({
          ...item,
          selected: msg.selectedPpfo?.includes(item.primaryKey) || false,
        }));

        const outcomes = rootOutcomes.map((item: any) => ({
          ...item,
          selected: msg.selectedPpfo?.includes(item.primaryKey) || false,
        }));

        const activeCustomer = rootActiveCustomer.map((item: any) => ({
          ...item,
          selected: msg.selectedActiveCustomer?.includes(item.primaryKey) || false,
        }));

        const productsAndServices = rootProductsAndServices.map((item: any) => ({
          ...item,
          selected: msg.selectedProductsAndServices?.includes(item.primaryKey) || false,
        }));

        const testimonials = rootTestimonials.map((item: any) => ({
          ...item,
          selected: msg.selectedTestimonials?.includes(item.primaryKey) || false,
        }));

        // Build transformed message without selectedPpfo/selectedActiveCustomer
        const {
          selectedPpfo,
          selectedActiveCustomer: selectedAC,
          selectedProductsAndServices,
          selectedTestimonials,
          ...restMsg
        } = msg;

        mergedMessages[msgType] = {
          ...restMsg,
          message: normalizeMessageByType(msgType, restMsg?.message),
          structuredMessage: msgType === 'callScriptMsg' ? restMsg?.message || null : null,
          justification:
            restMsg?.justification ||
            (msgType === 'callScriptMsg' ? restMsg?.message?.ai_reasoning || '' : ''),
          painpoint,
          features,
          outcomes,
          activeCustomer,
          productsAndServices,
          testimonials,
        };
      }
    });
  });

  // Return with contactPk as key
  return { [contactPk]: mergedMessages };
};

export const getCustomPromptData = createAsyncThunk<
  HPMCustomPromptProperties,
  { customerId: string; emailId: string }
>('hpm-v2/get-custom-prompt-data', async ({ customerId, emailId }) => {
  const res = await apiClient.post(
    GET_ALL_CUSTOM_PROMPT,
    {},
    {
      headers: { 'X-Login-Email': emailId, 'X-Customer-Id': customerId },
    },
  );
  return res.data;
});

export const saveCustomPrompt = createAsyncThunk<
  HPMCustomLibraryProperties,
  SaveCustomPromptPayload
>(
  'hpm-v2/save-custom-prompt-data',
  async ({
    customerId,
    emailId,
    promptName,
    promptDescription,
    llmModel,
    isEmail,
    isPostConnection,
    isCallScript,
    isFollowUp,
    isSecondFollowUp,
    isLinkedinPostFollowUp,
    isLinkedinPostSecondFollowUp,
    selectedColumns,
    selectedSignals,
    selectedProductsAndServices,
    selectedTestimonials,
    selectedHpmFileContents,
    selectedActiveCustomers,
  }) => {
    const res = await apiClient.post(
      SAVE_CUSTOM_PROMPT,
      {
        promptName,
        promptDescription,
        llmModel,
        isEmail,
        isPostConnection,
        isCallScript,
        selectedColumns,
        selectedSignals,
        isFollowUp,
        isSecondFollowUp,
        isLinkedinPostFollowUp,
        isLinkedinPostSecondFollowUp,
        selectedProductsAndServices,
        selectedTestimonials,
        selectedHpmFileContents,
        selectedActiveCustomers,
      },
      {
        headers: { 'X-Login-Email': emailId, 'X-Customer-Id': customerId },
      },
    );
    return res.data?.data;
  },
);

export const updateIsFavoriteCustomPrompt = createAsyncThunk<
  number,
  { customerId: string; emailId: string; primaryKey: number }
>('hpm-v2/update-is-favorite-custom-prompt-data', async ({ customerId, emailId, primaryKey }) => {
  await apiClient.post(
    MARK_CUSTOM_PROMPT,
    {
      primaryKey,
    },
    {
      headers: { 'X-Login-Email': emailId, 'X-Customer-Id': customerId },
    },
  );
  return primaryKey;
});

export const deleteCustomPrompt = createAsyncThunk<
  number,
  { customerId: string; emailId: string; primaryKey: number }
>('hpm-v2/delete-custom-prompt-data', async ({ customerId, emailId, primaryKey }) => {
  await apiClient.post(
    DELETE_CUSTOM_PROMPT,
    { primaryKey },
    { headers: { 'X-Login-Email': emailId, 'X-Customer-Id': customerId } },
  );
  return primaryKey;
});

export const editCustomPrompt = createAsyncThunk<
  HPMCustomLibraryProperties,
  EditCustomPromptPayload
>(
  'hpm-v2/edit-custom-prompt-data',
  async ({
    customerId,
    emailId,
    primaryKey,
    promptName,
    promptDescription,
    llmModel,
    isEmail,
    isFollowUp,
    isSecondFollowUp,
    isPostConnection,
    isCallScript,
    isLinkedinPostFollowUp,
    isLinkedinPostSecondFollowUp,
  }) => {
    const body = {
      primaryKey,
      promptName,
      promptDescription,
      llmModel,
      isEmail,
      isPostConnection,
      isCallScript,
      isFollowUp,
      isSecondFollowUp,
      isLinkedinPostFollowUp,
      isLinkedinPostSecondFollowUp,
    };
    const res = await apiClient.post(EDIT_CUSTOM_PROMPT, body, {
      headers: { 'X-Login-Email': emailId, 'X-Customer-Id': customerId },
    });
    return res.data?.data;
  },
);

export const generateHpm = createAsyncThunk<any, GenerateHpmPayload, { rejectValue: string }>(
  'hpm-v2/generate-hpm',
  async ({ customerId, emailId, contactInfoList, listId, customPromptDetail }) => {
    const res = await apiClient.post(
      GENERATE_HPM,
      {
        contactInfoList,
        listId,
        customPromptDetail,
      },
      {
        headers: { 'X-Login-Email': emailId, 'X-Customer-Id': customerId },
      },
    );
    return res.data;
  },
);

export const fetchContactDetails = createAsyncThunk<any, FetchContactDetailsPayload>(
  'hpm-v2/fetch-contact-details',
  async ({ customerId, emailId, dataPks, sort }) => {
    const res = await apiClient.post(
      GET_HPM_DATA,
      {
        dataPks,
        sort,
      },
      {
        headers: { 'X-Login-Email': emailId, 'X-Customer-Id': customerId },
      },
    );
    return res.data?.contacts || [];
  },
);

export const fetchContactDetailsPolling = createAsyncThunk<any, FetchContactDetailsPayload>(
  'hpm-v2/fetch-contact-details-polling',
  async ({ customerId, emailId, dataPks }) => {
    const res = await apiClient.post(
      GET_HPM_DATA,
      {
        dataPks,
      },
      {
        headers: { 'X-Login-Email': emailId, 'X-Customer-Id': customerId },
      },
    );
    return res.data?.contacts || [];
  },
);

// Like fetchContactDetailsPolling but merges results into existing contactsData
// instead of replacing the whole list. Use this when refreshing a subset of contacts
// (e.g. SSE per-contact completions) so other contacts aren't wiped.
export const fetchContactDetailsPartial = createAsyncThunk<any, FetchContactDetailsPayload>(
  'hpm-v2/fetch-contact-details-partial',
  async ({ customerId, emailId, dataPks }) => {
    const res = await apiClient.post(
      GET_HPM_DATA,
      { dataPks },
      { headers: { 'X-Login-Email': emailId, 'X-Customer-Id': customerId } },
    );
    return res.data?.contacts || [];
  },
);

export const fetchMessages = createAsyncThunk<any, FetchMessagesPayload>(
  'hpm-v2/fetch-messages',
  async ({ customerId, emailId, dataPks, contactPk }) => {
    const res = await apiClient.post(
      GET_HPM_MSG_DATA,
      {
        messagePks: dataPks,
      },
      {
        headers: { 'X-Login-Email': emailId, 'X-Customer-Id': customerId },
      },
    );

    return transformMessagesResponse(res.data, contactPk);
  },
);

export const fetchMessagesPolling = createAsyncThunk<any, FetchMessagesPayload>(
  'hpm-v2/fetch-messages-polling',
  async ({ customerId, emailId, dataPks, contactPk }) => {
    const res = await apiClient.post(
      GET_HPM_MSG_DATA,
      {
        messagePks: dataPks,
      },
      {
        headers: { 'X-Login-Email': emailId, 'X-Customer-Id': customerId },
      },
    );

    return transformMessagesResponse(res.data, contactPk);
  },
);


export const generateCallScriptMessage = createAsyncThunk<
  any,
  { customerId: string; emailId: string; contactPk: number; messagePk: number }
>('hpm-v2/generate-call-script-message', async ({ customerId, emailId, contactPk, messagePk }) => {
  const res = await apiClient.post(
    GENERATE_CALL_SCRIPT_MSG,
    { contactPk, messagePk },
    { headers: { 'X-Login-Email': emailId, 'X-Customer-Id': customerId } },
  );
  return res.data;
});

export const getContactDetails = createAsyncThunk<
  any,
  { customerId: string; linkedInUrl: string; contactPk: number }
>('hpm-v2/get-contact-details', async ({ customerId, linkedInUrl }) => {
  const response = await apiClient.get(
    `${GET_CONTACT_DETAILS}?linkedin_url=${linkedInUrl?.replace('https://', '').replace('www.', '') || ''
    }`,
    {
      headers: {
        'X-CUSTOMER-ID': customerId,
      },
    },
  );
  return { fetchedAt: new Date().toISOString(), ...response.data };
});

export const updateHpmContactData = createAsyncThunk<void, SaveHpmdataPayload>(
  'hpm-save-hpm-contact-data',
  async (payload) => {
    await apiClient.post(UPDATE_HPM_DATA, payload, {
      headers: { 'X-Login-Email': payload.userEmail, 'X-Customer-Id': payload.customerId },
    });
  },
);

export const retryHpm = createAsyncThunk<any, RetryHpmPayloadType>(
  'hpm-v2/re-try-hpm',
  async (payload) => {
    const { customerId, emailId, messagePks, listId } = payload;
    try {
      const res = await apiClient.post(
        RETRY_HPM,
        {
          messagePks,
          listId,
        },
        { headers: { 'X-Login-Email': emailId, 'X-Customer-Id': customerId } },
      );
      return { messagePks, orderId: res.data?.orderId ?? null };
    } catch (e: any) {
      throw new Error(e.response.data.message);
    }
  },
);

export const regenerateHpm = createAsyncThunk<any, RegenerateHpmPayloadType>(
  'hpm-v2/regenerate-hpm',
  async (payload) => {
    const { customerId, emailId, body } = payload;
    try {
      const res = await apiClient.post(REGENERATE_HPM, body, {
        headers: { 'X-Login-Email': emailId, 'X-Customer-Id': customerId },
      });
      return res.data;
    } catch (e: any) {
      throw new Error(e.response.data.message);
    }
  },
);

export const generateFreshHpm = createAsyncThunk<any, RegenerateHpmPayloadType>(
  'hpm-v2/generate-fresh-hpm',
  async (payload) => {
    const { customerId, emailId, body } = payload;
    try {
      const res = await apiClient.post(GENERATE_FRESH_HPM, body, {
        headers: { 'X-Login-Email': emailId, 'X-Customer-Id': customerId },
      });
      return res.data;
    } catch (e: any) {
      throw new Error(e.response.data.message);
    }
  },
);

export const generateHpmForFollowUps = createAsyncThunk<any, GenerateHpmForFollowUpsPayloadType>(
  'hpm-v2/generate-hpm-for-follow-ups',
  async (payload) => {
    const { customerId, emailId, contactPk, emailMsgPk, followUpMsgPk, followUpMsg, secondFollowUpMsg } =
      payload;
    const res = await apiClient.post(
      GENERATE_HPM_FOR_FOLLOW_UPS,
      {
        contactPk,
        emailMsgPk,
        followUpMsgPk,
        followUpMsg,
        secondFollowUpMsg,
      },
      {
        headers: { 'X-Login-Email': emailId, 'X-Customer-Id': customerId },
      },
    );
    return res.data;
  },
);

export const generateHpmForPostLinkedinFollowUps = createAsyncThunk<
  any,
  GenerateHpmForPostLinkedinFollowUpsPayloadType
>('hpm-v2/generate-hpm-for-post-linkedin-follow-ups', async (payload) => {
  const {
    customerId,
    emailId,
    contactPk,
    postLinkedinMsgPk,
    postLinkedinFollowUpMsgPk,
    postLinkedinFollowUpMsg,
    postLinkedinSecondFollowUpMsg,
  } = payload;
  const res = await apiClient.post(
    GENERATE_HPM_FOR_LINKEDIN_FOLLOW_UPS,
    {
      contactPk,
      postLinkedinMsgPk,
      postLinkedinFollowUpMsgPk,
      postLinkedinFollowUpMsg,
      postLinkedinSecondFollowUpMsg,
    },
    {
      headers: { 'X-Login-Email': emailId, 'X-Customer-Id': customerId },
    },
  );
  return res.data;
});
